import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {
    BehaviorSubject,
    merge,
    Observable,
    of,
    shareReplay,
    switchMap,
    tap,
} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {MessageService} from 'primeng/api';
import {environment} from '@/environments/environment';
import {CookieService} from "@/app/services/cookie.service";
import moment from 'moment-timezone';
import {
    Contact,
    NodePerformance,
    NodePerformanceAPI, parsePerf,
    Product,
    Response
} from "@/app/model";
import {APIData} from '../model/common';
import {NodePerformanceIot} from "@/app/model/iot";
import {uniqBy} from "lodash-es";
import {EventNodeAPI} from "@/app/model/interface/event-node";
import {MqttConnectionService} from "@/app/services/mqtt-connection.service";

@Injectable({
    providedIn: 'root'
})
export class ChartServices {

    DEBUG = false;

    http: HttpClient = inject(HttpClient)

    constructor(
        private cookieService: CookieService,
        private toaster: MessageService,
        private mqttConnectionService: MqttConnectionService
    ) {
    }

    private chartUpdate = new BehaviorSubject<any>({});

    chartUpdate$ = this.chartUpdate.asObservable();
    public emsUrl = environment.emsUrl;

    getProduct(product_id: string): Observable<Product | {}> {
        return this.http
            .get<Response<Product>>(`${this.emsUrl}/v1/product/${product_id}`, {
                withCredentials: true,
            })
            .pipe(
                map((product: Response<Product>) => {
                    const {data} = product;
                    return data;
                }),
                catchError((err) => {
                    // no need to retry, we're already in a polling loop
                    console.log(err);
                    this.toaster.add({
                        key: "connection-error",
                        severity: "error",
                        sticky: false,
                    });
                    return of({});
                })
            );
    }

    getContacts(site_id: string): Observable<Contact[] | []> {
        return this.http
            .get<Response<Contact[]>>(`${this.emsUrl}/v1/event_node/site/${site_id}/contacts`, {
                withCredentials: true,
            })
            .pipe(
                map((contact: Response<Contact[]>) => {
                    const {data} = contact;
                    return data;
                }),
                catchError((err) => {
                    // no need to retry, we're already in a polling loop
                    console.log(err);
                    this.toaster.add({
                        key: "connection-error",
                        severity: "error",
                        sticky: false,
                    });
                    return of([]);
                })
            );
    }

    getRegistration(registration_id: string) {
        return this.http
            .get<Response<any[]>>(`${this.emsUrl}/v1/registration/${registration_id}`, {
                withCredentials: true,
            })
            .pipe(
                map((registration: any) => {
                    return registration;
                }),
                catchError((err) => {
                    // no need to retry, we're already in a polling loop
                    console.log(err);
                    this.toaster.add({
                        key: "connection-error",
                        severity: "error",
                        sticky: false,
                    });
                    return of({});
                })
            );
    }

    getNodePerformances(
        node: EventNodeAPI,
        product: Product,
        startDateTime: moment.Moment,
        endDateTime: moment.Moment | null,
        subscribeToRealtimeUpdates: boolean = true
    ): Observable<NodePerformance[]> {
        const session = this.cookieService.getCookie('enoc_session');
        const headers = new HttpHeaders().append('enoc_session', session);
        let perfValues: NodePerformance[] = [];

        let frequency = 300000;
        if (product && product.reporting_interval_ms != null && product.reporting_interval_ms > 0) {
            frequency = Math.max(product.reporting_interval_ms, 60000);
        }
        const frequencyInMinutes = frequency / 60000;

        startDateTime.startOf('minute').minute(frequencyInMinutes * Math.round(startDateTime.minute() / frequencyInMinutes));
        endDateTime?.startOf('minute').minute(frequencyInMinutes * Math.round(endDateTime.minute() / frequencyInMinutes));

        // With no known end (event still pending), gap-fill only up to the latest
        // real data point we've seen instead of padding placeholders into the future.
        const resolveIntervals = (perf: NodePerformance[]): string[] => {
            if (endDateTime) {
                return this.determineIntervals(startDateTime.clone(), endDateTime, frequency);
            }
            if (!perf.length) {
                return [];
            }
            const lastIntervalMs = Math.max(...perf.map((p) => p.interval.time));
            const openEndDateTime = moment(lastIntervalMs).add(frequency, 'milliseconds');
            return this.determineIntervals(startDateTime.clone(), openEndDateTime, frequency);
        };

        const init$ = this.http.get<APIData<NodePerformanceAPI[]>>(`${(this.emsUrl)}/v1/event_node_performance/${node.registration_id}/${node.event_node_id}`, {withCredentials: true}).pipe(
            map(({data}) => data),
            map((intervalData) => this.massageTheIntervals(intervalData, product)),
            map((nodes) => nodes.map((evn) => parsePerf(evn))),
            map((perf) => this.gapFilling(perf, resolveIntervals(perf), node.event_id, node.site_id, node.event_node_id)),
            map((resp) => {
                perfValues = resp;
                return resp;
            }),
            tap((events) => this.DEBUG && console.log('performance-api | init', events)),
            catchError((err) => { // no need to retry, we're already in a polling loop
                console.log(err);
                this.toaster.add({key: 'connection-error', severity: 'error', sticky: false});
                return of<NodePerformance[]>([]);
            }),
            shareReplay(1),
        );

        const pubsub$ = init$.pipe(
            switchMap(() => {
                return new Observable<NodePerformance[]>((observer) => {
                    this.mqttConnectionService.subscribeToTopic(
                        [node.event_node_id],
                        'NODE_PERFORMANCE',
                        (iotUpdate: NodePerformanceIot) => {
                            const updatedPerf = this.updateNodes(perfValues, iotUpdate);
                            const filledPerf = this.gapFilling(updatedPerf, resolveIntervals(updatedPerf), node.event_id, node.site_id, node.event_node_id);
                            perfValues = filledPerf;
                            this.DEBUG && console.log('performance-api | pubsub', filledPerf);
                            observer.next(filledPerf);
                        }
                    );
                });
            }),
            shareReplay(1)
        );

        let performances$: Observable<NodePerformance[]>
        if (!subscribeToRealtimeUpdates || product && product.reporting_interval_ms < 60000) {
            performances$ = init$.pipe(
                shareReplay(1),
            );
        } else {
            performances$ = merge(init$, pubsub$).pipe(
                shareReplay(1),
            );
        }

        return performances$;
    }

    updateNodes(performances: NodePerformance[], update: NodePerformanceIot): NodePerformance[] {
        const node = performances.find(({interval}) => new Date(update.interval_dttm_utc).getTime() === interval.time);
        return node && performances.map((perf) => (perf === node) ? parsePerf({...node.model, ...update} as NodePerformanceAPI) : perf) || performances;
    }

    massageTheIntervals(intervalData: NodePerformanceAPI[], product: Product) {
        if (product && product.reporting_interval_ms < 60000) {
            intervalData.forEach((i) => {
                i.interval_dttm_utc = moment(i.interval_dttm_utc).startOf('minute').toISOString();
            })
            return uniqBy(intervalData, 'interval_dttm_utc')
        }
        return intervalData;
    }


    gapFilling(performances: NodePerformance[], intervals: string[], eventId: string, siteId: string, nodeId: string): NodePerformance[] {
        return intervals.map((interval) => {
            const predicate = performances.find((a) => a.model.interval_dttm_utc == interval);
            if (predicate) {
                return predicate;
            } else {
                return parsePerf({
                    event_node_id: nodeId,
                    site_id: siteId,
                    event_id: eventId,
                    interval_dttm_utc: interval,
                    interval_dttm_utc_program_locale: 'en_US', // setting it to default for now
                    metered_value: null,
                    metered_uom: null,
                    baseline_value: null,
                    baseline_uom: null,
                    target_value: null,
                    lower_target_value: null,
                    target_uom: null,
                    upper_target_value: null,
                    adjusted_baseline_value: null,
                } as NodePerformanceAPI);
            }
        });
    }

    determineIntervals(startTime: moment.Moment, endTime: moment.Moment, granularity: number): string[] {
        const range: string[] = [];
        while (startTime < endTime) {
            range.push(startTime.toISOString());
            startTime.add(granularity, 'milliseconds');
        }
        return range;
    }

    unsubscribeFromNodePerformance(event_node_id: string): void {
        this.mqttConnectionService.unsubscribe([event_node_id], 'NODE_PERFORMANCE');
    }

}
